import { AlarmOff, Article, Business, Email, Numbers, Person, Update } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { Box, Card, CardContent, CardHeader, Chip, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PendingRespondent, PendingRespondentStatusMap } from 'src/@types/pendingRespondentStatus';
import { formatterDateTime } from 'src/utils/formatDateTime';
import { joinWithFallback } from 'src/utils/labelUtils';

type IdIssuanceManagementDetailCardProps = {
  selectedUser: PendingRespondent;
  statusMap: PendingRespondentStatusMap;
};

export const IdIssuanceManagementDetailCard = ({ selectedUser, statusMap }: IdIssuanceManagementDetailCardProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid item md={6} xs={12} sx={{ width: '100%' }}>
        <Card sx={{ width: '100%', minHeight: 300 }}>
          <CardHeader title={t('idIssuanceManagement.detail.userInfo')} />
          <CardContent>
            <List sx={{ width: '100%' }}>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText primary={selectedUser.email} />
              </ListItem>
              {selectedUser.userLastNameEng && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <Person />
                    </ListItemIcon>
                    <ListItemText primary={joinWithFallback(selectedUser.userLastNameJpn, selectedUser.userFirstNameJpn)} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText primary={joinWithFallback(selectedUser.userLastNameKana, selectedUser.userFirstNameKana)} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText primary={joinWithFallback(selectedUser.userFirstNameEng, selectedUser.userLastNameEng)} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>
                    <ListItemText primary={joinWithFallback(selectedUser.companyNameJpn, selectedUser.companyCode)} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText primary={selectedUser.companyNameEng ? `${selectedUser.companyNameEng}` : '-'} />
                  </ListItem>
                </>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} xs={12} sx={{ width: '100%' }}>
        <Card sx={{ height: '100%' }}>
          <CardHeader title={t('idIssuanceManagement.detail.enqueteInfo')} />
          <CardContent>
            <List sx={{ width: '100%' }}>
              <ListItem>
                <ListItemIcon>
                  <Numbers />
                </ListItemIcon>
                <ListItemText primary={`${selectedUser.respondentRequestId}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Article />
                </ListItemIcon>
                <ListItemText primary={`${selectedUser.enqueteTitleJpn}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 24, height: 24 }} />
                </ListItemIcon>
                <ListItemText primary={`${selectedUser.enqueteTitleEng}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CalendarMonthIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${formatterDateTime(selectedUser.respondentRequestOpenedAt)} ～ ${formatterDateTime(
                    selectedUser.respondentRequestClosedAt,
                  )} `}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={12}>
        <Card>
          <CardHeader title={t('idIssuanceManagement.detail.status')} />
          <CardContent>
            <List sx={{ width: '100%' }}>
              <ListItem>
                <Chip label={statusMap[selectedUser.status]} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Update />
                </ListItemIcon>
                <ListItemText primary={`${formatterDateTime(selectedUser.updatedAt)}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AlarmOff />
                </ListItemIcon>
                <ListItemText primary={`${formatterDateTime(selectedUser.expiresAt)}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ForwardToInboxIcon />
                </ListItemIcon>
                <ListItemText primary={`${formatterDateTime(selectedUser.sentAt)}` || '-'} />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};
